// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useDevice } from '../../context/DeviceContext';
import './Settings.css';

const backgroundOptions = [
  { type: 'default', label: '默认', description: '经典深色渐变' },
  { type: 'solid', label: '纯色', description: '使用自定义纯色' },
  { type: 'preset', label: '预设背景图', description: '全屏填充背景' },
];

const Settings = ({
  backgroundPreference,
  onBackgroundChange,
  backgroundPresets,
  uiTheme,
  onThemeChange,
}) => {
  const {
    deviceInfo,
    dataStore,
    isConnected,
    sender,
    protocolVersion,
  } = useDevice();
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [informationData, setInformationData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!dataStore) return;
    const config = dataStore.getData('deviceConfig');
    const info = dataStore.getData('informationData');
    if (config) setDeviceConfig(config);
    if (info) setInformationData(info);

    const onConfig = (data) => setDeviceConfig(data);
    const onInfo = (data) => setInformationData(data);
    dataStore.on('deviceConfig', onConfig);
    dataStore.on('informationData', onInfo);
    return () => {
      dataStore.off('deviceConfig', onConfig);
      dataStore.off('informationData', onInfo);
    };
  }, [dataStore]);

  const cards = useMemo(() => {
    const parsedCfg = deviceConfig?.parsed;
    const parsedInfo = informationData?.parsed;
    const battery =
      parsedInfo?.batteryLevel != null ? parsedInfo.batteryLevel + '%' : '—';
    const charging =
      parsedInfo?.chargingFlag === 1
        ? '充电中'
        : parsedInfo?.chargingFlag === 0
          ? '未充电'
          : '—';

    return [
      {
        title: '基本信息',
        items: [
          { label: '产品名称', value: deviceInfo?.productName || '未知设备' },
          { label: '连接状态', value: isConnected ? '已连接' : '未连接' },
          { label: '配置文件', value: parsedInfo?.profileIndex != null ? 'Profile ' + (parsedInfo.profileIndex + 1) : '—' },
          { label: '电量', value: battery },
          { label: '充电', value: charging },
        ],
      },
      {
        title: '固件版本',
        items: [
          { label: '协议版本', value: protocolVersion || 'N/A' },
          { label: '主固件', value: parsedCfg?.softwareVersion || 'N/A' },
          { label: 'MCU2', value: parsedCfg?.mcu2SoftwareVersion || 'N/A' },
          { label: 'TFT MCU3', value: parsedCfg?.tftMcu3Version || 'N/A' },
          { label: 'MCU4', value: parsedCfg?.mcu4Version || 'N/A' },
        ],
      },
    ];
  }, [
    deviceInfo,
    deviceConfig,
    informationData,
    isConnected,
    protocolVersion,
  ]);

  const handleRefresh = async () => {
    if (!sender || !isConnected) return;
    setRefreshing(true);
    try {
      await sender.getDeviceConfig();
      await sender.wait(80);
      await sender.getInformationData();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="set-page">
      <header className="set-header">
        <div>
          <h1 className="set-title">设置</h1>
          <p className="set-subtitle">设备信息与连接状态</p>
        </div>
        <button
          type="button"
          className="set-refresh-btn"
          onClick={handleRefresh}
          disabled={!isConnected || refreshing}
        >
          {refreshing ? '刷新中…' : '刷新信息'}
        </button>
      </header>

      <div className="set-scroll">
        <div className="set-hero">
          <div className="set-hero-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <rect x="7" y="2" width="10" height="16" rx="5" stroke="currentColor" strokeWidth="1.6" />
              <circle cx="12" cy="8" r="1.4" fill="currentColor" />
              <path d="M9 18v2.5M15 18v2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </div>
          <div className="set-hero-text">
            <div className="set-hero-name">{deviceInfo?.productName || '未知设备'}</div>
            <div className="set-hero-meta">
              <span className={'set-dot' + (isConnected ? ' on' : '')} />
              {isConnected ? '设备在线' : '设备离线'}
            </div>
          </div>
        </div>

        <section className="set-card set-theme-card">
          <div className="set-section-heading">
            <div>
              <h2 className="set-card-title">界面主题</h2>
              <p className="set-card-description">主题切换不会改变设备参数与背景设置</p>
            </div>
          </div>
          <div className="set-theme-options" role="radiogroup" aria-label="界面主题">
            {[
              { id: 'theme1', name: '主题 1', description: '经典深色侧栏布局' },
              { id: 'theme2', name: '主题 2', description: '轻盈卡片与底部导航' },
            ].map((theme) => (
              <button
                type="button"
                key={theme.id}
                className={`set-theme-option ${uiTheme === theme.id ? 'selected' : ''}`}
                role="radio"
                aria-checked={uiTheme === theme.id}
                onClick={() => onThemeChange(theme.id)}
              >
                <span className={`set-theme-preview ${theme.id}`} aria-hidden>
                  <i /><i /><i />
                </span>
                <span className="set-background-option-copy">
                  <strong>{theme.name}</strong>
                  <small>{theme.description}</small>
                </span>
                <span className="set-background-radio" aria-hidden />
              </button>
            ))}
          </div>
        </section>

        <section className="set-card set-appearance-card">
          <div className="set-section-heading">
            <div>
              <h2 className="set-card-title">网页背景</h2>
              <p className="set-card-description">选择应用于整个网页的背景样式</p>
            </div>
          </div>
          <div className="set-background-options" role="radiogroup" aria-label="网页背景">
            {backgroundOptions.map((option) => {
              const selected = backgroundPreference.type === option.type;
              return (
                <button
                  type="button"
                  className={'set-background-option' + (selected ? ' selected' : '')}
                  key={option.type}
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onBackgroundChange({
                    ...backgroundPreference,
                    type: option.type,
                  })}
                >
                  <span
                    className={`set-background-preview ${option.type}`}
                    style={option.type === 'preset'
                      ? { backgroundImage: `url(${
                          backgroundPresets.find(
                            (preset) => preset.id === backgroundPreference.preset
                          )?.image || backgroundPresets[0].image
                        })` }
                      : option.type === 'solid'
                        ? { background: backgroundPreference.color }
                        : undefined}
                    aria-hidden="true"
                  />
                  <span className="set-background-option-copy">
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </span>
                  <span className="set-background-radio" aria-hidden="true" />
                </button>
              );
            })}
          </div>
          {backgroundPreference.type === 'solid' && (
            <label className="set-color-control">
              <span>
                <strong>背景颜色</strong>
                <small>{backgroundPreference.color.toUpperCase()}</small>
              </span>
              <input
                type="color"
                value={backgroundPreference.color}
                onChange={(event) => onBackgroundChange({
                  ...backgroundPreference,
                  color: event.target.value,
                })}
                aria-label="选择纯色背景"
              />
            </label>
          )}
          {backgroundPreference.type === 'preset' && (
            <div className="set-preset-picker" role="radiogroup" aria-label="选择预设背景图">
              {backgroundPresets.map((preset) => {
                const selected = backgroundPreference.preset === preset.id;
                return (
                  <button
                    type="button"
                    className={'set-preset-option' + (selected ? ' selected' : '')}
                    key={preset.id}
                    role="radio"
                    aria-checked={selected}
                    onClick={() => onBackgroundChange({
                      ...backgroundPreference,
                      type: 'preset',
                      preset: preset.id,
                    })}
                  >
                    <span
                      className="set-preset-image"
                      style={{ backgroundImage: `url(${preset.image})` }}
                      aria-hidden="true"
                    />
                    <span>{preset.label}</span>
                    <span className="set-background-radio" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <div className="set-grid">
          {cards.map((card) => (
            <section className="set-card" key={card.title}>
              <h2 className="set-card-title">{card.title}</h2>
              <div className="set-list">
                {card.items.map((item) => (
                  <div className="set-row" key={item.label}>
                    <span className="set-label">{item.label}</span>
                    <span className="set-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

      </div>
    </div>
  );
};

export default Settings;
